import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/reports';

// Fetch comprehensive report
export const fetchComprehensiveReport = createAsyncThunk(
  'reports/fetchComprehensiveReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.period) params.append('period', filters.period);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axiosInstance.get(`${BASE_URL}/comprehensive/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Report Definitions
export const fetchReportDefinitions = createAsyncThunk(
  'reports/fetchReportDefinitions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.module) queryParams.append('module', params.module);
      if (params.show_shared) queryParams.append('show_shared', params.show_shared);
      
      const response = await axiosInstance.get(
        `${BASE_URL}/definitions/?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createReportDefinition = createAsyncThunk(
  'reports/createReportDefinition',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/definitions/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateReportDefinition = createAsyncThunk(
  'reports/updateReportDefinition',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/definitions/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteReportDefinition = createAsyncThunk(
  'reports/deleteReportDefinition',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/definitions/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Run Report
export const runReport = createAsyncThunk(
  'reports/runReport',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/definitions/${id}/run/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Scheduled Reports
export const fetchScheduledReports = createAsyncThunk(
  'reports/fetchScheduledReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/scheduled/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createScheduledReport = createAsyncThunk(
  'reports/createScheduledReport',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/scheduled/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateScheduledReport = createAsyncThunk(
  'reports/updateScheduledReport',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/scheduled/${id}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteScheduledReport = createAsyncThunk(
  'reports/deleteScheduledReport',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/scheduled/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Comparative Analysis
export const fetchComparativeAnalysis = createAsyncThunk(
  'reports/fetchComparativeAnalysis',
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('year1', params.year1);
      queryParams.append('year2', params.year2);
      if (params.module) queryParams.append('module', params.module);
      
      const response = await axiosInstance.get(
        `${BASE_URL}/comparative/?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Report Executions
export const fetchReportExecutions = createAsyncThunk(
  'reports/fetchReportExecutions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.report_definition) queryParams.append('report_definition', params.report_definition);
      
      const response = await axiosInstance.get(
        `${BASE_URL}/executions/?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState: {
    reportData: null,
    loading: false,
    error: null,
    // Advanced reporting
    reportDefinitions: [],
    scheduledReports: [],
    reportExecutions: [],
    currentReportResult: null,
    comparativeData: null,
    definitionsLoading: false,
    scheduledLoading: false,
    executionsLoading: false,
    comparativeLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReport: (state) => {
      state.reportData = null;
    },
    clearCurrentReportResult: (state) => {
      state.currentReportResult = null;
    },
    clearComparativeData: (state) => {
      state.comparativeData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Comprehensive report
      .addCase(fetchComprehensiveReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComprehensiveReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchComprehensiveReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Report Definitions
      .addCase(fetchReportDefinitions.pending, (state) => {
        state.definitionsLoading = true;
      })
      .addCase(fetchReportDefinitions.fulfilled, (state, action) => {
        state.definitionsLoading = false;
        state.reportDefinitions = action.payload;
      })
      .addCase(fetchReportDefinitions.rejected, (state, action) => {
        state.definitionsLoading = false;
        state.error = action.payload;
      })
      .addCase(createReportDefinition.fulfilled, (state, action) => {
        state.reportDefinitions.push(action.payload);
      })
      .addCase(updateReportDefinition.fulfilled, (state, action) => {
        const index = state.reportDefinitions.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.reportDefinitions[index] = action.payload;
        }
      })
      .addCase(deleteReportDefinition.fulfilled, (state, action) => {
        state.reportDefinitions = state.reportDefinitions.filter(r => r.id !== action.payload);
      })
      // Run Report
      .addCase(runReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(runReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReportResult = action.payload;
      })
      .addCase(runReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Scheduled Reports
      .addCase(fetchScheduledReports.pending, (state) => {
        state.scheduledLoading = true;
      })
      .addCase(fetchScheduledReports.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledReports = action.payload;
      })
      .addCase(fetchScheduledReports.rejected, (state, action) => {
        state.scheduledLoading = false;
        state.error = action.payload;
      })
      .addCase(createScheduledReport.fulfilled, (state, action) => {
        state.scheduledReports.push(action.payload);
      })
      .addCase(updateScheduledReport.fulfilled, (state, action) => {
        const index = state.scheduledReports.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.scheduledReports[index] = action.payload;
        }
      })
      .addCase(deleteScheduledReport.fulfilled, (state, action) => {
        state.scheduledReports = state.scheduledReports.filter(r => r.id !== action.payload);
      })
      // Comparative Analysis
      .addCase(fetchComparativeAnalysis.pending, (state) => {
        state.comparativeLoading = true;
      })
      .addCase(fetchComparativeAnalysis.fulfilled, (state, action) => {
        state.comparativeLoading = false;
        state.comparativeData = action.payload;
      })
      .addCase(fetchComparativeAnalysis.rejected, (state, action) => {
        state.comparativeLoading = false;
        state.error = action.payload;
      })
      // Report Executions
      .addCase(fetchReportExecutions.pending, (state) => {
        state.executionsLoading = true;
      })
      .addCase(fetchReportExecutions.fulfilled, (state, action) => {
        state.executionsLoading = false;
        state.reportExecutions = action.payload;
      })
      .addCase(fetchReportExecutions.rejected, (state, action) => {
        state.executionsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearReport, clearCurrentReportResult, clearComparativeData } = reportsSlice.actions;
export default reportsSlice.reducer;
